import type { ProFormColumnsType } from '@ant-design/pro-components';
import type { BaseFormSchemaProps } from 'pages/BtlApplicationFormPage/types';
import type { MessageKeys } from 'types/intl';

type OoExpensesFormSchemaProps = BaseFormSchemaProps;

function createMoneyField(
  formatMessage: OoExpensesFormSchemaProps['formatMessage'],
  labelKey: MessageKeys,
  dataIndex: string | string[],
  suffix?: string,
): ProFormColumnsType {
  return {
    title: formatMessage({ id: labelKey }),
    dataIndex,
    valueType: { type: 'money', locale: 'en-GB' },
    width: 300,
    fieldProps: {
      precision: 0,
      placeholder: '£',
      ...(suffix ? { suffix } : {}),
    },
  };
}

function createSectionHeader(formatMessage: OoExpensesFormSchemaProps['formatMessage'], labelKey: MessageKeys) {
  return {
    renderFormItem: () => <h3 className='-mb-4'>{formatMessage({ id: labelKey })}</h3>,
  };
}

export function ooExpensesFormSchema(props: OoExpensesFormSchemaProps): ProFormColumnsType[] {
  const { formatMessage } = props;

  return [
    {
      title: formatMessage({ id: 'applicationForm.DEPENDANTS.dependantsUnder13.label' }),
      dataIndex: 'numberOfDependents13OrUnder',
      valueType: 'digit',
      width: 200,
      fieldProps: {
        precision: 0,
      },
      formItemProps: {
        rules: [
          {
            required: true,
            type: 'number',
            min: 0,
            message: formatMessage({ id: 'common.error.requiredField' }),
          },
        ],
      },
    },
    {
      title: formatMessage({ id: 'applicationForm.DEPENDANTS.dependantsOver13.label' }),
      dataIndex: 'numberOfDependentsOver13',
      valueType: 'digit',
      width: 200,
      fieldProps: {
        precision: 0,
      },
      formItemProps: {
        rules: [
          {
            required: true,
            type: 'number',
            min: 0,
            message: formatMessage({ id: 'common.error.requiredField' }),
          },
        ],
      },
    },
    createSectionHeader(props.formatMessage, 'ooCase.expenses.householdIncome.header'),
    createMoneyField(props.formatMessage, 'ooCase.expenses.childBenefit.label', ['income', 'childBenefit'], 'yearly'),
    createMoneyField(
      props.formatMessage,
      'ooCase.expenses.childTaxCredit.label',
      ['income', 'childTaxCredit'],
      'yearly',
    ),
    createMoneyField(
      props.formatMessage,
      'ooCase.expenses.workingTaxCredit.label',
      ['income', 'workingTaxCredit'],
      'yearly',
    ),
    createMoneyField(
      props.formatMessage,
      'ooCase.expenses.industrialInjuriesDisablementBenefit.label',
      ['income', 'industrialInjuriesDisablementBenefit'],
      'yearly',
    ),
    createMoneyField(
      props.formatMessage,
      'ooCase.expenses.personalIndependencePayment.label',
      ['income', 'personalIndependencePayment'],
      'yearly',
    ),
    createMoneyField(props.formatMessage, 'applicationForm.HOUSEHOLD_SPENDINGS.essentialSpending.label', [
      'declaredExpenditure',
      'basicEssentialLiving',
    ]),
    createMoneyField(props.formatMessage, 'applicationForm.HOUSEHOLD_SPENDINGS.lifestyleSpending.label', [
      'declaredExpenditure',
      'basicQualityOfLiving',
    ]),
    createSectionHeader(props.formatMessage, 'ooCase.expenses.regularOutgoings.header'),
    createMoneyField(
      props.formatMessage,
      'applicationForm.HOUSEHOLD_OUTGOINGS.childcare.label',
      ['declaredExpenditure', 'childcare'],
      'monthly',
    ),
    createMoneyField(
      props.formatMessage,
      'applicationForm.HOUSEHOLD_OUTGOINGS.maintenance.label',
      ['declaredExpenditure', 'dependantMaintenance'],
      'monthly',
    ),
    createMoneyField(
      props.formatMessage,
      'applicationForm.HOUSEHOLD_OUTGOINGS.educationFees.label',
      ['declaredExpenditure', 'educationFees'],
      'monthly',
    ),
    createMoneyField(
      props.formatMessage,
      'applicationForm.TRAVEL_EXPENSES.travelSpending.label',
      ['declaredExpenditure', 'travel'],
      'monthly',
    ),
    createSectionHeader(props.formatMessage, 'ooCase.expenses.loansAndCommitments.header'),
    createMoneyField(props.formatMessage, 'applicationForm.LOAN_REPAYMENTS.securedLoans.label', [
      'creditCommitments',
      'securedLoans',
    ]),
    createMoneyField(props.formatMessage, 'applicationForm.LOAN_REPAYMENTS.unsecuredLoans.label', [
      'creditCommitments',
      'unsecuredLoans',
    ]),
    createMoneyField(props.formatMessage, 'applicationForm.CARDS.note', ['creditCommitments', 'creditCardBalance']),
    createMoneyField(
      props.formatMessage,
      'ooCase.expenses.studentLoans.label',
      ['creditCommitments', 'unsecuredLoansOnRevertDate'],
      'monthly',
    ),
  ];
}
